import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, MapPin, Phone, Mail, Plus, X, Save, Edit2, ArrowLeft, CheckCircle, Search } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useFacilityData } from '../hooks/booking/useBookingContextSlices';
import { facilitiesAPI } from '../services/api';
import { useForm } from '../hooks/useForm';

const ConfigureFacility = () => {
  const { facility, updateFacility } = useFacilityData();
  const [facilitiesList, setFacilitiesList] = useState([]);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
  const [editingFacilityId, setEditingFacilityId] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [facSearch, setFacSearch] = useState('');
  const [facStatusFilter, setFacStatusFilter] = useState('');
  const [facPage, setFacPage] = useState(1);
  const PAGE_SIZE = 20;

  const filteredFacilities = useMemo(() => {
    const q = facSearch.toLowerCase();
    return facilitiesList.filter(f => {
      if (q && !(f.name || '').toLowerCase().includes(q) && !(f.city || '').toLowerCase().includes(q)) return false;
      if (facStatusFilter === 'active' && f.is_active === false) return false;
      if (facStatusFilter === 'inactive' && f.is_active !== false) return false;
      return true;
    });
  }, [facilitiesList, facSearch, facStatusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredFacilities.length / PAGE_SIZE));
  const pagedFacilities = filteredFacilities.slice((facPage - 1) * PAGE_SIZE, facPage * PAGE_SIZE);

  const EMPTY_FACILITY = {
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    contactPhone: '',
    contactEmail: '',
    contactPerson: '',
    services: [],
  };

  const facilityValidate = useCallback((vals) => {
    const errs = {};
    if (!vals.name.trim()) errs.name = 'Facility name is required';
    if (!vals.description.trim()) errs.description = 'Description is required';
    if (!vals.address.trim()) errs.address = 'Address is required';
    if (!vals.city.trim()) errs.city = 'City is required';
    if (!vals.state.trim()) errs.state = 'State is required';
    if (!vals.zipCode.trim()) errs.zipCode = 'ZIP code is required';
    if (!vals.contactPerson.trim()) errs.contactPerson = 'Contact person name is required';
    if (!vals.contactPhone.trim()) {
      errs.contactPhone = 'Contact phone is required';
    } else if (vals.contactPhone.replace(/\D/g, '').length !== 10) {
      errs.contactPhone = 'Please enter a valid 10-digit phone number';
    }
    if (!vals.contactEmail.trim()) {
      errs.contactEmail = 'Contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(vals.contactEmail)) {
      errs.contactEmail = 'Please enter a valid email address';
    }
    return Object.keys(errs).length ? errs : null;
  }, []);

  const {
    values: facilityData,
    errors,
    touched,
    submitting: isSubmitting,
    submitError,
    handleBlur,
    handleSubmit: formHandleSubmit,
    setFieldValue,
    setValues: loadFacilityValues,
    reset: resetForm,
  } = useForm({
    initialValues: EMPTY_FACILITY,
    validate: facilityValidate,
    onSubmit: async (vals) => {
      const facilityPayload = {
        name: vals.name,
        description: vals.description,
        address: vals.address,
        city: vals.city,
        state: vals.state,
        zip_code: vals.zipCode,
        contact_phone: vals.contactPhone,
        contact_email: vals.contactEmail,
        contact_person: vals.contactPerson,
      };
      if (editingFacilityId) facilityPayload.id = editingFacilityId;

      const response = await updateFacility(facilityPayload);
      if (response && response.success) {
        setShowSuccess(true);
        await loadFacilities();
        setTimeout(() => {
          setShowSuccess(false);
          handleCancel();
        }, 2000);
      } else {
        throw new Error(response?.error || 'Failed to save facility');
      }
    },
  });

  // Load facilities list
  const loadFacilities = async () => {
    setIsLoadingFacilities(true);
    try {
      const response = await facilitiesAPI.get();
      console.log('📋 Loaded facilities response:', response);
      
      let facilities = [];
      if (response && response.success && Array.isArray(response.data)) {
        facilities = response.data;
      } else if (Array.isArray(response)) {
        facilities = response;
      } else if (response && response.data && !Array.isArray(response.data)) {
        // Single facility object
        facilities = [response.data];
      }
      
      console.log('📋 Setting facilities list:', facilities.length, 'facilities');
      setFacilitiesList(facilities);
    } catch (error) {
      console.error('Error loading facilities:', error);
      setFacilitiesList([]);
    } finally {
      setIsLoadingFacilities(false);
    }
  };

  // Load existing facility data
  const loadFacilityData = (facilityToLoad) => {
    if (facilityToLoad) {
      loadFacilityValues({
        name: facilityToLoad.name || '',
        description: facilityToLoad.description || '',
        address: facilityToLoad.address || '',
        city: facilityToLoad.city || '',
        state: facilityToLoad.state || '',
        zipCode: facilityToLoad.zip_code || '',
        contactPhone: facilityToLoad.contact_phone || '',
        contactEmail: facilityToLoad.contact_email || '',
        contactPerson: facilityToLoad.contact_person || '',
        services: Array.isArray(facilityToLoad.services) ? facilityToLoad.services : [],
      });
    }
  };

  // Load facilities on mount
  useEffect(() => {
    loadFacilities();
  }, []);

  // Load facility from context if available (for backward compatibility)
  useEffect(() => {
    if (facility && facility.name && viewMode === 'list' && !editingFacilityId) {
      loadFacilityData(facility);
    }
  }, [facility]);

  const handleAddFacility = () => {
    setEditingFacilityId(null);
    resetForm(EMPTY_FACILITY);
    setViewMode('form');
  };

  const handleEditFacility = (facilityToEdit) => {
    setEditingFacilityId(facilityToEdit.id);
    loadFacilityData(facilityToEdit);
    setViewMode('form');
  };

  const handleCancel = () => {
    setViewMode('list');
    setEditingFacilityId(null);
    resetForm(EMPTY_FACILITY);
  };

  // List View
  if (viewMode === 'list') {
    return (
      <DashboardLayout>
        <div className="max-w-6xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-ga-text-primary mb-2">Facility Management</h1>
              <p className="text-ga-text-muted">
                Manage your facilities and contact information
              </p>
            </div>
            <button
              onClick={handleAddFacility}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Facility</span>
            </button>
          </div>

          {/* Search & filter */}
          {!isLoadingFacilities && facilitiesList.length > 0 && (
            <div className="mb-4 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or city…"
                  value={facSearch}
                  onChange={e => { setFacSearch(e.target.value); setFacPage(1); }}
                  className="pl-9 pr-4 py-2 w-full rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-ga-primary/30"
                />
              </div>
              <select
                value={facStatusFilter}
                onChange={e => { setFacStatusFilter(e.target.value); setFacPage(1); }}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <span className="self-center text-sm text-ga-text-muted whitespace-nowrap">
                {filteredFacilities.length} facilit{filteredFacilities.length === 1 ? 'y' : 'ies'}
              </span>
            </div>
          )}

          {isLoadingFacilities ? (
            <div className="ga-card rounded-lg shadow-sm p-12 text-center">
              <p className="text-gray-500">Loading facilities...</p>
            </div>
          ) : facilitiesList.length === 0 ? (
            <div className="ga-card rounded-lg shadow-sm p-12 text-center">
              <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-ga-text-primary mb-2">No Facilities Found</h3>
              <p className="text-ga-text-muted mb-6">Get started by adding your first facility</p>
              <button
                onClick={handleAddFacility}
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <Plus className="h-5 w-5" />
                <span>Add Your First Facility</span>
              </button>
            </div>
          ) : (
            <div className="grid gap-6">
              {pagedFacilities.map((fac) => (
                <div key={fac.id} className="ga-card rounded-lg shadow-sm p-6 border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <Building2 className="h-6 w-6 text-ga-primary" />
                        <h3 className="text-xl font-semibold text-ga-text-primary">{fac.name}</h3>
                      </div>
                      
                      {fac.description && (
                        <p className="text-ga-text-muted mb-3">{fac.description}</p>
                      )}
                      
                      <div className="grid md:grid-cols-2 gap-4 text-sm text-ga-text-muted">
                        {fac.address && (
                          <div className="flex items-start space-x-2">
                            <MapPin className="h-4 w-4 mt-0.5 text-gray-400" />
                            <span>{fac.address}, {fac.city}, {fac.state} {fac.zip_code}</span>
                          </div>
                        )}
                        {fac.contact_phone && (
                          <div className="flex items-start space-x-2">
                            <Phone className="h-4 w-4 mt-0.5 text-gray-400" />
                            <span>{fac.contact_phone}</span>
                          </div>
                        )}
                        {fac.contact_email && (
                          <div className="flex items-start space-x-2">
                            <Mail className="h-4 w-4 mt-0.5 text-gray-400" />
                            <span>{fac.contact_email}</span>
                          </div>
                        )}
                        {fac.contact_person && (
                          <div className="flex items-start space-x-2">
                            <span className="text-gray-400">Contact:</span>
                            <span>{fac.contact_person}</span>
                          </div>
                        )}
                      </div>
                      
                      {fac.services && Array.isArray(fac.services) && fac.services.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-sm font-medium text-gray-700 mb-2">Services ({fac.services.length}):</p>
                          <div className="flex flex-wrap gap-2">
                            {fac.services.map((service, idx) => (
                              <span
                                key={service.id || idx}
                                className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-medium"
                              >
                                {service.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4 flex flex-col space-y-2">
                      <button
                        onClick={() => handleEditFacility(fac)}
                        className="px-4 py-2 bg-ga-primary text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2"
                      >
                        <Edit2 className="h-4 w-4" />
                        <span>Edit</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                onClick={() => setFacPage(p => Math.max(1, p - 1))}
                disabled={facPage === 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                ← Prev
              </button>
              <span className="text-sm text-ga-text-muted">Page {facPage} of {totalPages}</span>
              <button
                onClick={() => setFacPage(p => Math.min(totalPages, p + 1))}
                disabled={facPage === totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                Next →
              </button>
            </div>
          )}

          {/* Destructive actions (like clearing local data) live in Settings with confirmation. */}
        </div>
      </DashboardLayout>
    );
  }

  // Form View
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={handleCancel}
            className="mb-4 flex items-center space-x-2 text-ga-text-muted hover:text-ga-text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Facilities List</span>
          </button>
          <h1 className="text-3xl font-bold text-ga-text-primary mb-2">
            {editingFacilityId ? 'Edit Facility' : 'Add New Facility'}
          </h1>
          <p className="text-ga-text-muted">
            {editingFacilityId 
              ? 'Update facility details and contact information'
              : 'Set up your facility details and contact information'}
          </p>
        </div>

        {showSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800 flex items-center">
              <Save className="h-5 w-5 mr-2" />
              Facility {editingFacilityId ? 'updated' : 'saved'} successfully!
            </p>
          </div>
        )}

        <form onSubmit={formHandleSubmit} className="space-y-6">
          {/* Facility Name */}
          <div className="ga-card rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-ga-text-primary mb-4 flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-ga-primary" />
              Facility Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facility Name *
                  {editingFacilityId && (
                    <span className="ml-2 text-xs text-gray-500 font-normal">(Cannot be changed)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={facilityData.name}
                  onChange={(e) => setFieldValue('name', e.target.value)}
                  className={`input-field w-full ${editingFacilityId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="e.g., Mavericks Cricket Ground"
                  required
                  disabled={!!editingFacilityId}
                  readOnly={!!editingFacilityId}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facility Description *
                </label>
                <textarea
                  value={facilityData.description}
                  onChange={(e) => setFieldValue('description', e.target.value)}
                  rows="4"
                  className="input-field w-full"
                  placeholder="Describe your facility, amenities, and what makes it special..."
                  required
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="ga-card rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-ga-text-primary mb-4 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-ga-primary" />
              Address
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address *
                </label>
                <input
                  type="text"
                  value={facilityData.address}
                  onChange={(e) => setFieldValue('address', e.target.value)}
                  className="input-field w-full"
                  placeholder="e.g., 123 Sports Avenue"
                  required
                />
                {errors.address && (
                  <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                )}
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    value={facilityData.city}
                    onChange={(e) => setFieldValue('city', e.target.value)}
                    className="input-field w-full"
                    placeholder="City"
                    required
                  />
                  {errors.city && (
                    <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    value={facilityData.state}
                    onChange={(e) => setFieldValue('state', e.target.value)}
                    className="input-field w-full"
                    placeholder="State"
                    required
                  />
                  {errors.state && (
                    <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    value={facilityData.zipCode}
                    onChange={(e) => setFieldValue('zipCode', e.target.value)}
                    className="input-field w-full"
                    placeholder="ZIP Code"
                    required
                  />
                  {errors.zipCode && (
                    <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="ga-card rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-ga-text-primary mb-4 flex items-center">
              <Phone className="h-5 w-5 mr-2 text-ga-primary" />
              Contact Details
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person Name *
                </label>
                <input
                  type="text"
                  value={facilityData.contactPerson}
                  onChange={(e) => setFieldValue('contactPerson', e.target.value)}
                  className="input-field w-full"
                  placeholder="e.g., John Doe"
                  required
                />
                {errors.contactPerson && (
                  <p className="text-red-500 text-sm mt-1">{errors.contactPerson}</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="h-4 w-4 inline mr-1" />
                    Contact Phone *
                  </label>
                  <input
                    type="tel"
                    value={facilityData.contactPhone}
                    onChange={(e) => setFieldValue('contactPhone', e.target.value)}
                    className="input-field w-full"
                    placeholder="10-digit phone number"
                    required
                  />
                  {errors.contactPhone && (
                    <p className="text-red-500 text-sm mt-1">{errors.contactPhone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="h-4 w-4 inline mr-1" />
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    value={facilityData.contactEmail}
                    onChange={(e) => setFieldValue('contactEmail', e.target.value)}
                    className="input-field w-full"
                    placeholder="email@example.com"
                    required
                  />
                  {errors.contactEmail && (
                    <p className="text-red-500 text-sm mt-1">{errors.contactEmail}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {submitError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{submitError}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{isSubmitting ? 'Saving...' : editingFacilityId ? 'Update Facility' : 'Save Facility'}</span>
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default ConfigureFacility;
